import React, { useEffect, useState } from 'react';
import {
	NotificationDrawer,
	NotificationDrawerHeader,
	Spinner,
	Bullseye,
	Button,
	Grid,
	Card,
	CardBody,
	GridItem,
	AlertVariant,
} from '@patternfly/react-core';
import './Notifications.scss';
import { User } from '../../api/security/User';
import { TAppNotification, TJobDetails } from '../../api/types/AppNotification';
import { faEnvelopeCircleCheck, faX } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	dismissAllNotifications,
	markAllNotificationsAsRead,
} from '../../api/notification/Notification';
import NotificationsGroup from './NotificationsGroups';
import { useToast } from '@zeroedin-tech/zi-common-ui/lib';
import { useMount } from 'react-use';

const Notifications = () => {
	const [notifications, setNotifications] = useState<TAppNotification[]>([]);
	const [notificationsLoaded, setNotificationsLoaded] = useState(false);
	const [unreadCount, setUnreadCount] = useState<number>(0);
	const { addToast } = useToast();

	useMount(() => {
		User.GetNotifications()
			.then((notifications) => {
				notifications.map((notification) => {
					if (notification.job_details) {
						notification.job_details = JSON.parse(
							notification.job_details.toString()
						) as TJobDetails;
					}

					return notification;
				});

				setNotifications(notifications);
				setNotificationsLoaded(true);
			})
			.catch((error) => {
				console.error(error);
			});
	});

	useEffect(() => {
		if (notificationsLoaded) {
			setUnreadCount(notifications.filter((n) => !n.read).length);
			return;
		}
	}, [notifications, notificationsLoaded]);

	const markAllAsRead = () => {
		const newState = notifications.map((notification) => {
			notification.read = true;

			return notification;
		});

		setNotifications(newState);

		void markAllNotificationsAsRead().then(() => {
			addToast('All notifications successfully marked as read.', AlertVariant.success);
		});
	};

	const dismissAllNotificationsOnClick = () => {
		void dismissAllNotifications().then(() => {
			addToast('All notifications successfully dismissed.', AlertVariant.success);
		});
		setNotifications([]);
	};

	if (!notificationsLoaded) {
		return (
			<Bullseye style={{ paddingTop: '2rem' }}>
				<div>
					<Spinner
						isSVG
						size={'xl'}
					/>
				</div>
			</Bullseye>
		);
	}

	const groupBy = <T, K extends keyof any>(
		array: T[],
		keyGetter: (item: T) => K
	): Record<K, T[]> => {
		return array.reduce((result, currentValue) => {
			const key = keyGetter(currentValue);
			if (!result[key]) {
				result[key] = [];
			}
			result[key].push(currentValue);
			return result;
		}, {} as Record<K, T[]>);
	};

	const groupedNotifications = groupBy(
		notifications,
		(notification) => notification.job_details?.entityType ?? 'null'
	);

	return (
		<Card>
			<CardBody>
				<NotificationDrawer
					onPointerEnterCapture={undefined}
					onPointerLeaveCapture={undefined}
				>
					<NotificationDrawerHeader count={unreadCount}>
						<Button
							variant={'plain'}
							disabled={unreadCount === 0}
							title={'Click to mark as read'}
							onClick={() => markAllAsRead()}
						>
							<FontAwesomeIcon icon={faEnvelopeCircleCheck} />
						</Button>
						<Button
							variant={'plain'}
							title={'Click to dismiss(Delete) all notifications'}
							onClick={() => dismissAllNotificationsOnClick()}
						>
							<FontAwesomeIcon icon={faX} />
						</Button>
					</NotificationDrawerHeader>
					<Grid hasGutter>
						<GridItem span={12}>
							{Object.entries(groupedNotifications).map(
								([jobType, groupedNotifications]) => {
									return (
										<NotificationsGroup
											jobGroup={{
												jobType: jobType,
												notifications: groupedNotifications,
											}}
											notifications={notifications}
											setNotifications={setNotifications}
											groupBy={groupBy}
										/>
									);
								}
							)}
						</GridItem>
					</Grid>
				</NotificationDrawer>
			</CardBody>
		</Card>
	);
};

export default Notifications;
